import { toValue } from 'vue'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { BaseService } from '@common/http-layer/service/base.service'

import type {
  ActivityHeatmapResults,
  AnalyticsFilterBody,
  AnalyticsOverviewResults,
  AnalyticsResponse,
  AnalyticsTimeSeriesBody,
  AnalyticsCacheRefreshResponse,
  TimeseriesResults,
} from '../types/analytics.api'

/**
 * Community analytics API — dashboard reads + export + cache refresh.
 *
 * - Client Portal (WL): `/clientportal-middleware/community-analytics/{locationId}/groups/{groupId}/…`
 *   (portal JWT; middleware stamps `trafficAppSource: 'communities'`).
 * - GoKollab: `/community-analytics/{locationId}/groups/{groupId}/…`
 *   (BFF rewrites to `/gokollab/community-analytics/…` and stamps app source).
 *
 * Traffic page-view ingest stays on direct CA — see `community-traffic-page-view.utils.ts`.
 */
export class AnalyticsService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
      serviceName: 'AnalyticsService',
    })
  }

  private getBasePath(locationId: string, groupId: string): string {
    const loc = locationId?.trim()
    const gid = groupId?.trim()
    if (!loc) throw new Error('Location ID is required')
    if (!gid) throw new Error('Group ID is required')
    const prefix = this.isGokollabSurface() ? '/community-analytics' : '/clientportal-middleware/community-analytics'
    return `${prefix}/${loc}/groups/${gid}`
  }

  private isGokollabSurface(): boolean {
    return Boolean(toValue(useGlobal().isGokollab))
  }

  /** Strip FE trafficAppSource — WL middleware / GK BFF stamp it server-side. */
  private withSurfaceFields<T extends AnalyticsFilterBody | AnalyticsTimeSeriesBody>(body: T): T {
    const { trafficAppSource: _omit, ...rest } = body
    return rest as T
  }

  async getOverview(groupId: string, locationId: string, body: AnalyticsFilterBody): Promise<AnalyticsResponse<AnalyticsOverviewResults>> {
    const { data } = await this.post<AnalyticsResponse<AnalyticsOverviewResults>>(
      `${this.getBasePath(locationId, groupId)}/overview`,
      this.withSurfaceFields(body),
      { groupId, locationId }
    )
    return data
  }

  async getActivityHeatmap(
    groupId: string,
    locationId: string,
    body: AnalyticsFilterBody
  ): Promise<AnalyticsResponse<ActivityHeatmapResults>> {
    const { data } = await this.post<AnalyticsResponse<ActivityHeatmapResults>>(
      `${this.getBasePath(locationId, groupId)}/activity-heatmap`,
      this.withSurfaceFields(body),
      { groupId, locationId }
    )
    return data
  }

  async getTimeSeries(groupId: string, locationId: string, body: AnalyticsTimeSeriesBody): Promise<AnalyticsResponse<TimeseriesResults>> {
    const { data } = await this.post<AnalyticsResponse<TimeseriesResults>>(
      `${this.getBasePath(locationId, groupId)}/time-series`,
      this.withSurfaceFields(body),
      { groupId, locationId }
    )
    return data
  }

  async getInactiveMembersExport(groupId: string, locationId: string, body: AnalyticsFilterBody): Promise<Blob> {
    const { data } = await this.getBlob(`${this.getBasePath(locationId, groupId)}/export`, {
      params: {
        from: body.from,
        to: body.to,
        timezone: body.timezone,
      },
      groupId,
      locationId,
    })
    return data
  }

  async refreshCache(groupId: string, locationId: string): Promise<AnalyticsCacheRefreshResponse> {
    const { data } = await this.post<AnalyticsCacheRefreshResponse>(
      `${this.getBasePath(locationId, groupId)}/cache/refresh`,
      {},
      { groupId, locationId }
    )
    return data
  }
}
